import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaPlus, 
  FaTrash, 
  FaSave, 
  FaCheck, 
  FaUndo, 
  FaCalculator, 
  FaSearch,
  FaExclamationTriangle 
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

const SaisieEcriture = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // États principaux
  const [journaux, setJournaux] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [exerciceActuel, setExerciceActuel] = useState(null);

  // État du formulaire d'écriture
  const [ecriture, setEcriture] = useState({
    numero: '',
    date: new Date().toISOString().split('T')[0],
    journal_id: '',
    libelle: '',
    piece: {
      numero: '',
      type: 'autre',
      reference: ''
    },
    lignes: [
      { compte_id: '', libelle: '', debit: 0, credit: 0, tiers: '' },
      { compte_id: '', libelle: '', debit: 0, credit: 0, tiers: '' }
    ]
  });

  // États d'interface
  const [showCompteModal, setShowCompteModal] = useState(false);
  const [searchCompte, setSearchCompte] = useState('');
  const [selectedLigneIndex, setSelectedLigneIndex] = useState(null);
  const [errors, setErrors] = useState({});

  // Charger les données initiales
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Simulation des appels API
      setJournaux([
        { _id: '1', code: 'VE', libelle: 'Journal des Ventes', type: 'VE' },
        { _id: '2', code: 'AC', libelle: 'Journal des Achats', type: 'AC' },
        { _id: '3', code: 'BQ', libelle: 'Journal de Banque', type: 'BQ' },
        { _id: '4', code: 'CA', libelle: 'Journal de Caisse', type: 'CA' },
        { _id: '5', code: 'OD', libelle: 'Journal des Opérations Diverses', type: 'OD' }
      ]);

      setComptes([
        { _id: '1', code: '101', libelle: 'Capital social', classe_id: { code: '1' } },
        { _id: '2', code: '411', libelle: 'Clients', classe_id: { code: '4' } },
        { _id: '3', code: '401', libelle: 'Fournisseurs', classe_id: { code: '4' } },
        { _id: '4', code: '512', libelle: 'Banque', classe_id: { code: '5' } },
        { _id: '5', code: '531', libelle: 'Caisse', classe_id: { code: '5' } },
        { _id: '6', code: '601', libelle: 'Achats de marchandises', classe_id: { code: '6' } },
        { _id: '7', code: '701', libelle: 'Ventes de marchandises', classe_id: { code: '7' } }
      ]);

      setExerciceActuel({ annee: 2025, statut: 'ouvert' });
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculer les totaux
  const totaux = useCallback(() => {
    const totalDebit = ecriture.lignes.reduce((sum, ligne) => sum + parseFloat(ligne.debit || 0), 0);
    const totalCredit = ecriture.lignes.reduce((sum, ligne) => sum + parseFloat(ligne.credit || 0), 0);
    const difference = Math.abs(totalDebit - totalCredit);
    
    return { totalDebit, totalCredit, difference, equilibre: difference < 0.01 };
  }, [ecriture.lignes]);

  // Ajouter une ligne d'écriture
  const ajouterLigne = () => {
    setEcriture(prev => ({
      ...prev,
      lignes: [...prev.lignes, { compte_id: '', libelle: '', debit: 0, credit: 0, tiers: '' }]
    }));
  };

  // Supprimer une ligne d'écriture
  const supprimerLigne = (index) => {
    if (ecriture.lignes.length <= 2) return; // Minimum 2 lignes
    
    setEcriture(prev => ({
      ...prev,
      lignes: prev.lignes.filter((_, i) => i !== index)
    }));
  };

  // Mettre à jour une ligne
  const mettreAJourLigne = (index, field, value) => {
    const nouvelleLigne = { ...ecriture.lignes[index] };
    
    // Conversion des montants
    if (field === 'debit' || field === 'credit') {
      value = parseFloat(value) || 0;
      
      // Si on saisit un débit, remettre le crédit à 0 et vice versa
      if (field === 'debit' && value > 0) {
        nouvelleLigne.credit = 0;
      } else if (field === 'credit' && value > 0) {
        nouvelleLigne.debit = 0;
      }
    }

    // Mise à jour du libellé automatique si on change le compte
    if (field === 'compte_id') {
      const compte = comptes.find(c => c._id === value);
      if (compte && !nouvelleLigne.libelle) {
        nouvelleLigne.libelle = ecriture.libelle || compte.libelle;
      }
    }

    nouvelleLigne[field] = value;

    setEcriture(prev => ({
      ...prev,
      lignes: prev.lignes.map((ligne, i) => i === index ? nouvelleLigne : ligne)
    }));
  };

  // Ouvrir la modal de sélection de compte
  const ouvrirModalCompte = (index) => {
    setSelectedLigneIndex(index);
    setShowCompteModal(true);
    setSearchCompte('');
  };

  // Sélectionner un compte depuis la modal
  const selectionnerCompte = (compte) => {
    if (selectedLigneIndex !== null) {
      mettreAJourLigne(selectedLigneIndex, 'compte_id', compte._id);
      setShowCompteModal(false);
      setSelectedLigneIndex(null);
    }
  };

  // Générer le numéro d'écriture automatiquement
  const genererNumero = async (journalId) => {
    // Simulation - dans la vraie vie, appel API
    const journal = journaux.find(j => j._id === journalId);
    if (journal) {
      const numero = `${journal.code}${exerciceActuel.annee}000001`;
      setEcriture(prev => ({ ...prev, numero }));
    }
  };

  // Valider le formulaire
  const validerFormulaire = () => {
    const nouvellesTorreurs = {};
    
    if (!ecriture.date) nouvellesTorreurs.date = 'Date obligatoire';
    if (!ecriture.journal_id) nouvellesTorreurs.journal_id = 'Journal obligatoire';
    if (!ecriture.libelle.trim()) nouvellesTorreurs.libelle = 'Libellé obligatoire';
    
    // Validation des lignes
    ecriture.lignes.forEach((ligne, index) => {
      if (!ligne.compte_id) {
        nouvellesTorreurs[`ligne_${index}_compte`] = 'Compte obligatoire';
      }
      if (!ligne.libelle.trim()) {
        nouvellesTorreurs[`ligne_${index}_libelle`] = 'Libellé obligatoire';
      }
      if (ligne.debit === 0 && ligne.credit === 0) {
        nouvellesTorreurs[`ligne_${index}_montant`] = 'Montant obligatoire';
      }
    });
    
    // Vérifier l'équilibre
    const { equilibre } = totaux();
    if (!equilibre) {
      nouvellesTorreurs.equilibre = 'L\'écriture doit être équilibrée';
    }
    
    setErrors(nouvellesTorreurs);
    return Object.keys(nouvellesTorreurs).length === 0;
  };

  // Sauvegarder l'écriture
  const sauvegarder = async (statut = 'brouillon') => {
    if (!validerFormulaire()) return;

    try {
      setSaving(true);
      
      const ecritureData = {
        ...ecriture,
        statut,
        exercice: exerciceActuel.annee,
        created_by: currentUser._id
      };

      console.log('Sauvegarde écriture:', ecritureData);
      // Appel API ici
      // await comptabiliteService.creerEcriture(ecritureData);
      
      alert(`Écriture ${statut === 'validee' ? 'validée' : 'sauvegardée'} avec succès`);
      
      // Réinitialiser le formulaire
      if (statut === 'validee') {
        reinitialiserFormulaire();
      }
      
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const reinitialiserFormulaire = () => {
    setEcriture({
      numero: '',
      date: new Date().toISOString().split('T')[0],
      journal_id: '',
      libelle: '',
      piece: { numero: '', type: 'autre', reference: '' },
      lignes: [
        { compte_id: '', libelle: '', debit: 0, credit: 0, tiers: '' },
        { compte_id: '', libelle: '', debit: 0, credit: 0, tiers: '' }
      ]
    });
    setErrors({});
  };

  // Filtrer les comptes pour la recherche
  const comptesFiltres = comptes.filter(compte =>
    compte.code.toLowerCase().includes(searchCompte.toLowerCase()) ||
    compte.libelle.toLowerCase().includes(searchCompte.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;

  const { totalDebit, totalCredit, difference, equilibre } = totaux();

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Nouvelle Écriture Comptable
        </h2>

        {/* Informations générales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <input
              type="date"
              value={ecriture.date}
              onChange={(e) => setEcriture(prev => ({ ...prev, date: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.date ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Journal *
            </label>
            <select
              value={ecriture.journal_id}
              onChange={(e) => {
                setEcriture(prev => ({ ...prev, journal_id: e.target.value }));
                genererNumero(e.target.value);
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.journal_id ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner...</option>
              {journaux.map(journal => (
                <option key={journal._id} value={journal._id}>
                  {journal.code} - {journal.libelle}
                </option>
              ))}
            </select>
            {errors.journal_id && <p className="text-red-500 text-xs mt-1">{errors.journal_id}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numéro
            </label>
            <input
              type="text"
              value={ecriture.numero}
              onChange={(e) => setEcriture(prev => ({ ...prev, numero: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              placeholder="Automatique"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pièce N°
            </label>
            <input
              type="text"
              value={ecriture.piece.numero}
              onChange={(e) => setEcriture(prev => ({ 
                ...prev, 
                piece: { ...prev.piece, numero: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="N° pièce justificative"
            />
          </div>
        </div>

        {/* Libellé général */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Libellé général *
          </label>
          <input
            type="text"
            value={ecriture.libelle}
            onChange={(e) => setEcriture(prev => ({ ...prev, libelle: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.libelle ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Description générale de l'opération"
          />
          {errors.libelle && <p className="text-red-500 text-xs mt-1">{errors.libelle}</p>}
        </div>
      </div>

      {/* Lignes d'écriture */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Lignes d'écriture</h3>
          <button
            onClick={ajouterLigne}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <FaPlus />
            <span>Ajouter ligne</span>
          </button>
        </div>

        {/* Tableau des lignes */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compte
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Libellé
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tiers
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Débit
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Crédit
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ecriture.lignes.map((ligne, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {/* Compte */}
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => ouvrirModalCompte(index)}
                        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 min-w-0"
                      >
                        <FaSearch className="text-gray-400 flex-shrink-0" />
                        <span className="truncate">
                          {ligne.compte_id ? 
                            comptes.find(c => c._id === ligne.compte_id)?.code || 'Compte introuvable' :
                            'Sélectionner...'
                          }
                        </span>
                      </button>
                    </div>
                    {errors[`ligne_${index}_compte`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`ligne_${index}_compte`]}</p>
                    )}
                  </td>

                  {/* Libellé */}
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={ligne.libelle}
                      onChange={(e) => mettreAJourLigne(index, 'libelle', e.target.value)}
                      className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors[`ligne_${index}_libelle`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Libellé de l'opération"
                    />
                    {errors[`ligne_${index}_libelle`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`ligne_${index}_libelle`]}</p>
                    )}
                  </td>

                  {/* Tiers */}
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={ligne.tiers}
                      onChange={(e) => mettreAJourLigne(index, 'tiers', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Nom du tiers"
                    />
                  </td>

                  {/* Débit */}
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ligne.debit || ''}
                      onChange={(e) => mettreAJourLigne(index, 'debit', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0,00"
                    />
                  </td>

                  {/* Crédit */}
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ligne.credit || ''}
                      onChange={(e) => mettreAJourLigne(index, 'credit', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0,00"
                    />
                    {errors[`ligne_${index}_montant`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`ligne_${index}_montant`]}</p>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-center">
                    {ecriture.lignes.length > 2 && (
                      <button
                        onClick={() => supprimerLigne(index)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer la ligne"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totaux et équilibrage */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <FaCalculator className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  Total Débit: <span className="font-medium">{totalDebit.toLocaleString()} XOF</span>
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Total Crédit: <span className="font-medium">{totalCredit.toLocaleString()} XOF</span>
              </div>
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                equilibre ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {equilibre ? <FaCheck /> : <FaExclamationTriangle />}
                <span>
                  {equilibre ? 'Équilibrée' : `Différence: ${difference.toLocaleString()} XOF`}
                </span>
              </div>
            </div>
          </div>
          {errors.equilibre && (
            <p className="text-red-500 text-sm mt-2">{errors.equilibre}</p>
          )}
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="flex justify-between items-center">
        <button
          onClick={reinitialiserFormulaire}
          className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <FaUndo />
          <span>Réinitialiser</span>
        </button>

        <div className="flex space-x-3">
          <button
            onClick={() => sauvegarder('brouillon')}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <FaSave />
            <span>{saving ? 'Sauvegarde...' : 'Sauvegarder brouillon'}</span>
          </button>

          <button
            onClick={() => sauvegarder('validee')}
            disabled={saving || !equilibre}
            className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaCheck />
            <span>{saving ? 'Validation...' : 'Valider et enregistrer'}</span>
          </button>
        </div>
      </div>

      {/* Modal de sélection de compte */}
      {showCompteModal && (
        <Modal
          isOpen={showCompteModal}
          onClose={() => setShowCompteModal(false)}
          title="Sélectionner un compte"
          size="large"
        >
          <div className="space-y-4">
            {/* Barre de recherche */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchCompte}
                onChange={(e) => setSearchCompte(e.target.value)}
                placeholder="Rechercher par code ou libellé..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>

            {/* Liste des comptes */}
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
              {comptesFiltres.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Aucun compte trouvé
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {comptesFiltres.map((compte) => (
                    <button
                      key={compte._id}
                      onClick={() => selectionnerCompte(compte)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">
                            {compte.code}
                          </span>
                          <span className="ml-2 text-gray-600">
                            {compte.libelle}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          Classe {compte.classe_id?.code}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowCompteModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SaisieEcriture;
